import { DeckGrid } from '@/components/DeckGrid';
import { SkeletonGrid, SkeletonLine } from '@/components/Skeleton';
import { usePageTitle } from '@/hooks/usePageTitle';
import { decks, results, type AnalysisResult } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

function titleCase(s: string): string {
	return s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function analysisIcon(type: string | undefined): string {
	switch (type) {
		case 'power_level': return 'bolt';
		case 'optimization': return 'auto_fix_high';
		case 'primer': return 'menu_book';
		case 'roast':
		case 'deck_roast': return 'local_fire_department';
		default: return 'analytics';
	}
}

const HOW_IT_WORKS = [
	{
		icon: 'upload',
		step: '01',
		title: 'Import a Deck',
		desc: 'Paste a Moxfield, Archidekt, or plain-text URL. We pull the list instantly.',
	},
	{
		icon: 'bolt',
		step: '02',
		title: 'Click Analyze',
		desc: 'Hit Analyze and grab a coffee. Power level, combos, and card notes in under a minute.',
	},
	{
		icon: 'menu_book',
		step: '03',
		title: 'Read the Primer',
		desc: 'A crafted primer explains your strategy, win-cons, and how to pilot the deck.',
	},
	{
		icon: 'auto_fix_high',
		step: '04',
		title: 'Stay Sharp',
		desc: 'Re-analyze after new sets drop. Card suggestions update as the meta evolves.',
	},
];

const FEATURES = [
	{
		icon: 'bolt',
		title: 'Power Level Analysis',
		desc: 'Bracket-aware scoring aligned with the official WotC system. Fast mana, combos, game changers — all measured.',
	},
	{
		icon: 'construction',
		title: 'Build Assist',
		desc: 'Start from a commander, a bracket, and your themes. See role targets for ramp, draw, and interaction, then fill each slot from ranked suggestions.',
	},
	{
		icon: 'new_releases',
		title: "What's New",
		desc: 'Cards from recent and upcoming sets, ranked for your specific deck. Swap in the ones that beat what you\'re running.',
	},
	{
		icon: 'auto_fix_high',
		title: 'Optimization',
		desc: 'Cut-and-add recommendations grounded in your actual deck, its themes, and your budget constraints.',
	},
	{
		icon: 'travel_explore',
		title: 'Find a Home',
		desc: 'Just opened a new card? Paste the name and see which of your decks wants it most — with specific suggested cuts.',
	},
	{
		icon: 'menu_book',
		title: 'Deck Primer',
		desc: 'Auto-generate a shareable primer that explains your strategy, key combos, and how to pilot the deck.',
	},
];

export function HomeView() {
	usePageTitle('Home');
	const { user } = useAuth();

	const { data: recentDecks, isLoading: decksLoading } = useQuery({
		queryKey: ['decks', 'list'],
		queryFn: () => decks.list(),
		staleTime: 30_000,
	});

	const { data: recentAnalyses, isLoading: analysesLoading } = useQuery({
		queryKey: ['recentAnalyses'],
		queryFn: () => results.list(5),
		staleTime: 30_000,
	});

	return (
		<div className="home-view max-w-[960px] mx-auto px-6 py-8">

			{/* ── Hero ── */}
			<div className="text-center mb-14">
				<img
					src="/static/images/meta-command-logo.png"
					alt="MetaCommand"
					className="h-[96px] object-contain mb-5"
				/>
				<h1 className="text-on-surface text-[32px] font-bold m-0 mb-3 leading-tight">
					Your Commander decks,<br className="hidden sm:block" /> understood.
				</h1>
				<p className="text-on-surface-variant text-lg max-w-[520px] mx-auto mb-2 leading-relaxed">
					AI-powered analysis, deck building, and power-level assessment for Commander/EDH.
				</p>
				<p className="text-on-surface-variant text-sm max-w-[440px] mx-auto mb-8 opacity-70">
					Free to use. No subscription required. Access to many features requires an account,
					but creating one is quick and free — no credit card needed.
				</p>
				<div className="flex gap-3 justify-center flex-wrap">
					<Link
						to="/decks"
						className="py-3 px-7 bg-primary text-on-primary rounded-pill font-semibold text-[15px] no-underline"
					>
						Browse Decks
					</Link>
					{!user && (
						<Link
							to="/login"
							className="py-3 px-7 bg-surface-container-high text-on-surface rounded-pill font-semibold text-[15px] no-underline"
						>
							Get Started Free
						</Link>
					)}
					{user && (
						<Link
							to="/my-decks"
							className="py-3 px-7 bg-surface-container-high text-on-surface rounded-pill font-semibold text-[15px] no-underline"
						>
							My Decks
						</Link>
					)}
				</div>
			</div>

			{/* ── How it works ── */}
		<section className="mb-14">
			<div className="flex flex-col sm:flex-row items-stretch gap-0 rounded-xl bg-surface-container border border-outline-variant/60 overflow-hidden">
				{HOW_IT_WORKS.map((s, i) => (
					<div key={s.step} className="flex sm:flex-col flex-row items-start sm:items-center gap-4 sm:gap-2 p-5 flex-1 relative">
						{/* connector arrow between steps */}
						{i < HOW_IT_WORKS.length - 1 && (
							<span
								className="hidden sm:flex material-symbols-outlined text-outline-variant absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 bg-surface-container"
								style={{ fontSize: '18px' }}
							>
								chevron_right
							</span>
						)}
						<div className="flex sm:flex-col items-center sm:items-center gap-3 sm:gap-2 w-full">
							<div className="flex flex-col items-center gap-1 shrink-0">
								<span className="text-[10px] font-bold text-primary tracking-widest opacity-70">{s.step}</span>
								<span
									className="material-symbols-outlined text-primary"
									style={{ fontSize: '24px', lineHeight: 1 }}
								>
									{s.icon}
								</span>
							</div>
							<div className="sm:text-center">
								<div className="font-semibold text-on-surface text-[14px] mb-1">{s.title}</div>
								<div className="text-xs text-on-surface-variant leading-relaxed">{s.desc}</div>
							</div>
						</div>
					</div>
				))}
			</div>
		</section>

		{/* ── Feature grid ── */}
			<section className="mb-16">
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{FEATURES.map(f => (
						<div
							key={f.title}
							className="flex flex-col gap-2 p-5 rounded-xl bg-surface-container border border-outline-variant/60"
						>
							<span
								className="material-symbols-outlined text-primary"
								style={{ fontSize: '22px', lineHeight: 1 }}
							>
								{f.icon}
							</span>
							<div className="font-semibold text-on-surface text-[15px]">{f.title}</div>
							<div className="text-sm text-on-surface-variant leading-relaxed">{f.desc}</div>
						</div>
					))}
				</div>
			</section>

			{/* ── Recent public decks ── */}
			{decksLoading && (
				<section className="mb-12">
					<h2 className="text-on-surface text-xl font-semibold mb-4">Recent Decks</h2>
					<SkeletonGrid count={4} />
				</section>
			)}

			{!decksLoading && recentDecks && recentDecks.length > 0 && (
				<section className="mb-12">
					<h2 className="text-on-surface text-xl font-semibold mb-4">Recent Decks</h2>
					<DeckGrid decks={recentDecks} groupMode="recent" maxDecks={8} />
					<div className="text-center mt-6">
						<Link to="/decks" className="text-primary text-sm">
							View all decks →
						</Link>
					</div>
				</section>
			)}

			{/* ── Recent Analyses ── */}
			{analysesLoading && (
				<section>
					<h2 className="text-on-surface text-xl font-semibold mb-4">Recent Analyses</h2>
					<div className="flex flex-col gap-2">
						{Array.from({ length: 3 }, (_, i) => (
							<SkeletonLine key={i} height="48px" />
						))}
					</div>
				</section>
			)}

			{!analysesLoading && recentAnalyses && recentAnalyses.length > 0 && (
				<section>
					<h2 className="text-on-surface text-xl font-semibold mb-4">Recent Analyses</h2>
					<div className="flex flex-col gap-2">
						{recentAnalyses.map((a: AnalysisResult) => (
							<Link
								key={a.id}
								to={`/deck/${a.deck_id}`}
								className="flex justify-between items-center py-3 px-4 bg-surface-container rounded-sm no-underline border border-outline-variant"
								style={{
									transition: 'background-color 0.15s ease, border-left-color 0.15s ease',
									borderLeft: '3px solid transparent',
								}}
								onMouseEnter={e => {
									(e.currentTarget as HTMLElement).style.backgroundColor = 'var(--surface-container-highest)';
									(e.currentTarget as HTMLElement).style.borderLeftColor = 'var(--primary)';
								}}
								onMouseLeave={e => {
									(e.currentTarget as HTMLElement).style.backgroundColor = '';
									(e.currentTarget as HTMLElement).style.borderLeftColor = 'transparent';
								}}
							>
								<div>
									<div className="font-bold text-on-surface text-sm">{a.deck_name}</div>
									{a.analysis_type && (
										<div className="flex items-center gap-1 text-xs text-on-surface-variant mt-0.5">
											<span
												className="material-symbols-outlined"
												style={{ fontSize: '14px', opacity: 0.7, lineHeight: 1 }}
											>
												{analysisIcon(a.analysis_type)}
											</span>
											{titleCase(a.analysis_type)}
										</div>
									)}
								</div>
								{a.created_at && (
									<div className="text-xs text-on-surface-variant shrink-0">
										{new Date(a.created_at).toLocaleDateString()}
									</div>
								)}
							</Link>
						))}
					</div>
				</section>
			)}
		</div>
	);
}
